/*
  Generic admin table.
  columns: [{ key, label, render?(row), width? }]
  rows:    array of objects (needs a unique `id`)
  groups:  optional [{ key, label, meta?, rows: [] }] — renders a
           labelled band above each block of rows instead of one flat
           list. Pass either `rows` or `groups`, not both.
*/
export default function DataTable({
  columns,
  rows,
  groups,
  loading,
  emptyMessage = 'No records found.',
  onRowClick,
}) {
  if (loading) {
    return <div className="admin-table-state">Loading…</div>;
  }

  const grouped = Array.isArray(groups);
  const total = grouped
    ? groups.reduce((n, g) => n + (g.rows?.length || 0), 0)
    : rows?.length || 0;

  if (total === 0) {
    return <div className="admin-table-state">{emptyMessage}</div>;
  }

  const bodyRow = (row) => (
    <tr
      key={row.id}
      className={onRowClick ? 'clickable' : undefined}
      onClick={onRowClick ? () => onRowClick(row) : undefined}
    >
      {columns.map((col) => (
        <td key={col.key}>
          {col.render ? col.render(row) : row[col.key] ?? '—'}
        </td>
      ))}
    </tr>
  );

  return (
    <div className="admin-table-wrap">
      <table className="admin-table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} style={col.width ? { width: col.width } : undefined}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>

        {grouped ? (
          // A tbody per group, so the band and its rows stay together.
          groups.map((g) => (
            <tbody key={g.key}>
              <tr className="group-row">
                <td colSpan={columns.length}>
                  <span className="group-label">{g.label}</span>
                  {g.meta && <span className="group-meta">{g.meta}</span>}
                </td>
              </tr>
              {g.rows.map(bodyRow)}
            </tbody>
          ))
        ) : (
          <tbody>{rows.map(bodyRow)}</tbody>
        )}
      </table>
    </div>
  );
}
